import { stableSerialize } from '@/utils/stableSerialize';
import type { Database, Json } from '@/integrations/supabase/types';
import {
  PROPERTY_TYPE_CATALOG,
  isSubtypeAllowedForType,
  type PropertyTypeCode,
} from '@/types/property.types';

import type { PublicPropertyFilters, MapBounds } from './types';

const RENT_ONLY_OPERATION = 'renta';
type PublicPropertiesRpcArgs = Database['api']['Functions']['public_properties_list_v2']['Args'];
type PublicPropertiesRpcArgsWithRequirements = PublicPropertiesRpcArgs & {
  p_without_guarantor?: boolean;
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const cleanStringArray = (value?: string[] | null): string[] | null => {
  if (!Array.isArray(value)) return null;

  const cleaned = [...new Set(value.map((x) => x?.trim()).filter(Boolean) as string[])];

  return cleaned.length ? cleaned : null;
};

const cleanNumber = (value?: number | null): number | null => {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const cleanBoolean = (value?: boolean | null): boolean | null => {
  return typeof value === 'boolean' ? value : null;
};

const FEATURE_TO_INTERNAL_CHARACTERISTIC_LABEL_MAP: Record<string, string> = {
  'Balcón': 'Balcón',
  Terraza: 'Terraza privada',
  'Roof garden': 'Roof garden privado',
  Bodega: 'Bodega Incluida',
};

const INTERNAL_CHARACTERISTIC_LABEL_ALIASES: Record<string, string> = {
  'Cuarto de lavado': 'Área de lavado',
  Estudio: 'Área de estudio',
};

const AMENITY_TO_INTERNAL_CHARACTERISTIC_LABEL_MAP: Record<string, string> = {
  'Aire acondicionado': 'Aire acondicionado',
};

const isCanonicalPropertyType = (value: string): value is PropertyTypeCode => {
  return Object.prototype.hasOwnProperty.call(PROPERTY_TYPE_CATALOG, value);
};

const cleanCanonicalPropertyTypes = (
  value?: string[] | null
): PropertyTypeCode[] | null => {
  const cleaned = cleanStringArray(value)?.filter(isCanonicalPropertyType) ?? [];
  return cleaned.length ? cleaned : null;
};

const cleanCanonicalPropertySubtypes = (
  propertyTypes: PropertyTypeCode[] | null,
  value?: string[] | null
): string[] | null => {
  if (!propertyTypes || propertyTypes.length !== 1) return null;

  const [selectedType] = propertyTypes;
  const cleaned = cleanStringArray(value)?.filter((entry) => isSubtypeAllowedForType(selectedType, entry)) ?? [];

  return cleaned.length ? cleaned : null;
};

const resolveListingSearchText = (filters: PublicPropertyFilters) => {
  if (isNonEmptyString(filters.searchText)) return filters.searchText.trim();
  if (filters.colonias?.length === 1) return filters.colonias[0];
  if (filters.alcaldias?.length === 1) return filters.alcaldias[0];
  if (filters.cities?.length === 1) return filters.cities[0];
  return null;
};

const mapBoundsToJson = (value: PublicPropertyFilters['mapBounds']): Json | undefined => {
  if (!value) return undefined;

  return {
    south: value.south,
    west: value.west,
    north: value.north,
    east: value.east,
  };
};

const normalizeOperationTypes = (value?: string[] | null): string[] => {
  if (!Array.isArray(value) || value.length === 0) {
    return [RENT_ONLY_OPERATION];
  }

  return value
    .map((entry) => (typeof entry === 'string' ? entry.trim().toLowerCase() : ''))
    .filter((entry) => entry === RENT_ONLY_OPERATION);
};

export const normalizePublicPropertyFilters = (
  filters?: PublicPropertyFilters
): PublicPropertyFilters => {
  const f = filters ?? {};
  const propertyTypes = cleanCanonicalPropertyTypes(f.propertyTypes);
  const propertySubtypes = cleanCanonicalPropertySubtypes(propertyTypes, f.propertySubtypes);
  const rawAmenities = cleanStringArray(f.amenities);
  const rawServices = cleanStringArray(f.services);
  const rawFeatures = cleanStringArray(f.features);
  const rawInternalCharacteristics = cleanStringArray(f.internalCharacteristics);
  const rawServiciosIncluidos = cleanStringArray(f.serviciosIncluidos);

  const migratedAmenities = rawAmenities?.filter(
    (value) => !Object.prototype.hasOwnProperty.call(AMENITY_TO_INTERNAL_CHARACTERISTIC_LABEL_MAP, value)
  ) ?? null;

  const migratedFeatureSelections = rawFeatures?.filter(
    (value) => !Object.prototype.hasOwnProperty.call(FEATURE_TO_INTERNAL_CHARACTERISTIC_LABEL_MAP, value)
  ) ?? null;

  const normalizedInternalCharacteristics = [
    ...(rawInternalCharacteristics?.map(
      (value) => INTERNAL_CHARACTERISTIC_LABEL_ALIASES[value] ?? value
    ) ?? []),
    ...(rawAmenities
      ?.map((value) => AMENITY_TO_INTERNAL_CHARACTERISTIC_LABEL_MAP[value])
      .filter(Boolean) ?? []),
    ...(rawFeatures
      ?.map((value) => FEATURE_TO_INTERNAL_CHARACTERISTIC_LABEL_MAP[value])
      .filter(Boolean) ?? []),
  ];

  const cleanedInternalCharacteristics =
    normalizedInternalCharacteristics.length > 0
      ? [...new Set(normalizedInternalCharacteristics)]
      : null;

  return {
    searchText: isNonEmptyString(f.searchText) ? f.searchText.trim() : null,
    publicId: cleanNumber(f.publicId),
    discounted: cleanBoolean(f.discounted),
    operationTypes: normalizeOperationTypes(f.operationTypes),
    propertyTypes,
    propertySubtypes,
    cities: cleanStringArray(f.cities),
    alcaldias: cleanStringArray(f.alcaldias),
    colonias: cleanStringArray(f.colonias),
    minPrice: cleanNumber(f.minPrice),
    maxPrice: cleanNumber(f.maxPrice),
    minArea: cleanNumber(f.minArea),
    maxArea: cleanNumber(f.maxArea),
    bedrooms: cleanNumber(f.bedrooms),
    minBathrooms: cleanNumber(f.minBathrooms),
    minHalfBathrooms: cleanNumber(f.minHalfBathrooms),
    minParkingSpots: cleanNumber(f.minParkingSpots),
    furnished: cleanBoolean(f.furnished),
    semiFurnished: cleanBoolean(f.semiFurnished),
    petsAllowed: cleanBoolean(f.petsAllowed),
    withoutGuarantor: cleanBoolean(f.withoutGuarantor),
    amenities: migratedAmenities,
    services: rawServices,
    features: migratedFeatureSelections,
    internalCharacteristics: cleanedInternalCharacteristics,
    serviciosIncluidos: rawServiciosIncluidos,
    mapBounds:
      f.mapBounds &&
      cleanNumber(f.mapBounds.south) !== null &&
      cleanNumber(f.mapBounds.west) !== null &&
      cleanNumber(f.mapBounds.north) !== null &&
      cleanNumber(f.mapBounds.east) !== null
        ? {
            south: f.mapBounds.south,
            west: f.mapBounds.west,
            north: f.mapBounds.north,
            east: f.mapBounds.east,
          }
        : null,
    centerLat: cleanNumber(f.centerLat),
    centerLng: cleanNumber(f.centerLng),
    radiusMeters: cleanNumber(f.radiusMeters),
  };
};

export const buildPublicPropertiesRpcParams = (
  filters?: PublicPropertyFilters,
  options?: {
    limit?: number;
    offset?: number;
    includeTotalCount?: boolean;
    includeHalfBathroomFilters?: boolean;
  }
): PublicPropertiesRpcArgsWithRequirements => {
  const f = normalizePublicPropertyFilters(filters);

  return {
    p_limit: options?.limit ?? 20,
    p_offset: options?.offset ?? 0,
    p_include_total_count: options?.includeTotalCount ?? false,
    p_search_text: resolveListingSearchText(f) ?? undefined,
    p_public_id: f.publicId ?? undefined,
    p_only_discounted: f.discounted ?? undefined,
    p_operation_types: [RENT_ONLY_OPERATION],
    p_property_types: f.propertyTypes ?? undefined,
    p_property_subtypes: f.propertySubtypes ?? undefined,
    p_alcaldias: f.alcaldias ?? undefined,
    p_colonias: f.colonias ?? undefined,
    p_min_price: f.minPrice ?? undefined,
    p_max_price: f.maxPrice ?? undefined,
    p_min_area: f.minArea ?? undefined,
    p_max_area: f.maxArea ?? undefined,
    p_bedrooms: f.bedrooms ?? undefined,
    p_min_bathrooms: f.minBathrooms ?? undefined,
    p_min_half_bathrooms: options?.includeHalfBathroomFilters ? f.minHalfBathrooms ?? undefined : undefined,
    p_min_parking_spots: f.minParkingSpots ?? undefined,
    p_furnished: f.furnished ?? undefined,
    p_semi_furnished: f.semiFurnished ?? undefined,
    p_pets_allowed: f.petsAllowed ?? undefined,
    p_without_guarantor: f.withoutGuarantor ?? undefined,
    p_amenities: f.amenities ?? undefined,
    p_services: f.services ?? undefined,
    p_features: f.features ?? undefined,
    p_internal_characteristics: f.internalCharacteristics ?? undefined,
    p_servicios_incluidos: f.serviciosIncluidos ?? undefined,
    p_map_bounds: mapBoundsToJson(f.mapBounds),
    p_center_lat: f.centerLat ?? undefined,
    p_center_lng: f.centerLng ?? undefined,
    p_radius_meters: f.radiusMeters ?? undefined,
  };
};

export const stableFiltersForKey = (filters?: PublicPropertyFilters) => {
  return stableSerialize(normalizePublicPropertyFilters(filters));
};

const MAP_BOUNDS_ROUND_DECIMALS = 4;

const roundMapCoordinate = (value: number): number => {
  const factor = 10 ** MAP_BOUNDS_ROUND_DECIMALS;
  return Math.round(value * factor) / factor;
};

/** Round map bounds to stabilize TanStack Query keys across micro-pan movements. */
export const roundMapBounds = (bounds: PublicPropertyFilters['mapBounds']): MapBounds | null => {
  if (!bounds) return null;
  return {
    south: roundMapCoordinate(bounds.south),
    west: roundMapCoordinate(bounds.west),
    north: roundMapCoordinate(bounds.north),
    east: roundMapCoordinate(bounds.east),
  };
};
